import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Tooltip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Stage, Layer, Image as KonvaImage, Rect, Circle, Group, Text, Path } from 'react-konva';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import WarningIcon from '@mui/icons-material/Warning';
import boundingBoxApi from '../../../api/boundingBoxApi';
import { useTheme } from '@mui/material/styles';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
// Import authenticated API service instead of raw axios
import { default as api } from '../../../services/api';

// Set base URL for API
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Annotation modes
const MODE = {
  VIEW: 'view',
  ANNOTATE: 'annotate',
};

// Status colors for display
const STATUS_COLORS = {
  'draft': '#FFC107', // amber
  'validated': '#4CAF50', // green
  'rejected': '#F44336', // red
  'new': '#2196F3' // blue - for defect status
};

// Color for defect type label
const DEFECT_TYPE_COLOR = '#3f51b5'; // primary blue

// Anchor points for resizing
const ANCHORS = [
  { x: 0, y: 0, cursor: 'nw-resize' },   // top-left
  { x: 0.5, y: 0, cursor: 'n-resize' },  // top-center
  { x: 1, y: 0, cursor: 'ne-resize' },   // top-right
  { x: 1, y: 0.5, cursor: 'e-resize' },  // right-center
  { x: 1, y: 1, cursor: 'se-resize' },   // bottom-right
  { x: 0.5, y: 1, cursor: 's-resize' },  // bottom-center
  { x: 0, y: 1, cursor: 'sw-resize' },   // bottom-left
  { x: 0, y: 0.5, cursor: 'w-resize' },  // left-center
];

// Box style configuration
const BOX_STYLE = {
  // STATUS-BASED STYLES (overrides mode-based styles)
  STATUS: {
    DRAFT: {
      stroke: '#FFC107', // amber
      strokeWidth: 2,
      dash: [5, 5], // Always dashed for draft
    },
    VALIDATED: {
      stroke: '#4CAF50', // green
      strokeWidth: 2,
      dash: null, // Always solid for validated
    },
    REJECTED: {
      stroke: '#F44336', // red  
      strokeWidth: 2,
      dash: [2, 2], // Short dashes for rejected
    }
  },
  // ANNOTATION MODE STYLES (used when status style doesn't apply)
  ANNOTATE: {
    NORMAL: {
      stroke: 'yellow',
      strokeWidth: 2,
      dash: [5, 5], // Creates dotted line effect
    },
    SELECTED: {
      stroke: 'orange',
      strokeWidth: 3,
      dash: [5, 5],
    },
    DRAWING: {
      stroke: 'yellow',
      strokeWidth: 2,
      dash: [5, 5],
    }
  },
  // VIEW MODE STYLES (used when status style doesn't apply)
  VIEW: {
    NORMAL: {
      stroke: 'blue',
      strokeWidth: 2,
      dash: null, // Solid line
    }
  }
};

// Helper function to convert polygon points to SVG path
const pointsToPath = (points) => {
  if (!points || points.length === 0) return '';
  let path = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    path += ` L ${points[i].x} ${points[i].y}`;
  }
  path += ' Z';
  return path;
};

const DefectViewerModal = ({ open, onClose, defect, onError }) => {
  const [image, setImage] = useState(null);
  const [mode, setMode] = useState(MODE.VIEW);

  const [isDrawing, setIsDrawing] = useState(false);
  const [annotations, setAnnotations] = useState([]);
  const [currentAnnotation, setCurrentAnnotation] = useState(null);
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [activeAnchorIndex, setActiveAnchorIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPosition, setDragStartPosition] = useState(null);
  
  // Add zoom state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 });
  const [isZoomed, setIsZoomed] = useState(false);
  
  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const isAnchorDraggingRef = useRef(false);
  const imageObjRef = useRef(null);

  // Add state for API-related feedback
  const [apiStatus, setApiStatus] = useState({ open: false, severity: 'info', message: '' });
  
  // Defect photo data state
  const [photoData, setPhotoData] = useState(null);

  // Get box style based on current mode and selection state
  const getBoxStyle = (isSelected, status, isPrimary = false) => {
    // Primary defect gets special styling
    if (isPrimary) {
      return {
        stroke: '#ff4444',
        strokeWidth: 3,
        dash: [5, 5]
      };
    }
    
    // First priority: Status-based styling (for draft, validated, rejected)
    if (status === 'draft') {
      return BOX_STYLE.STATUS.DRAFT;
    } else if (status === 'validated') {
      return BOX_STYLE.STATUS.VALIDATED;
    } else if (status === 'rejected') {
      return BOX_STYLE.STATUS.REJECTED;
    }

    // Second priority: Mode-based styling
    if (mode === MODE.ANNOTATE) {
      return isSelected 
        ? BOX_STYLE.ANNOTATE.SELECTED 
        : BOX_STYLE.ANNOTATE.NORMAL;
    } else {
      return isSelected 
        ? BOX_STYLE.VIEW.SELECTED 
        : BOX_STYLE.VIEW.NORMAL;
    }
  };

  // Load image using the same method as Enhanced_ImageViewerModal
  const loadImage = async () => {
    console.log('[DefectViewerModal] loadImage() called with photoData:', photoData);
    if (!photoData || !photoData.photo.photo_path) {
      console.error("[DefectViewerModal] No photo or photo URL provided");
      setLoadError("No photo provided");
      setIsLoading(false);
      return;
    }

    console.log("[DefectViewerModal] Attempting to load image via presigned URL for key:", photoData.photo.photo_path);
    setIsLoading(true);
    setLoadError(null);
    setAnnotations([]);
    setImage(null);

    try {
      // Step 1: Get the presigned URL from our backend using a POST request
      const presignedUrlResponse = await api.post(`/uploads/get-secure-url`, {
        path: photoData.photo.photo_path
      });
      const presignedUrl = presignedUrlResponse.data.url;

      if (!presignedUrl) {
        throw new Error("Presigned URL was not returned from the server.");
      }

      // Step 2: Fetch the image from the presigned URL as a blob
      // We use a direct axios call here because the presigned URL is an absolute URL to S3
      const imageResponse = await axios.get(presignedUrl, {
        responseType: 'blob',
      });

      // Step 3: Create a local object URL from the blob
      const objectURL = URL.createObjectURL(imageResponse.data);

      // Step 4: Create a new image object and set its source to the blob URL
      const img = new window.Image();
      imageObjRef.current = img;

      img.onload = () => {
        console.log("Image loaded successfully from presigned URL:", img.width, img.height);
        setImage(img);
        setIsLoading(false);
        // Update stage size immediately after image load if container is ready
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          if (rect.width && rect.height) {
            console.log('[DefectViewerModal] Setting stage size after image load:', { width: rect.width, height: rect.height });
            setStageSize({ width: rect.width, height: rect.height });
          }
        }
        URL.revokeObjectURL(objectURL); // Clean up memory
      };

      img.onerror = (e) => {
        console.error("Error loading image from blob URL:", e);
        setLoadError("Failed to display fetched image. Please try again.");
        setIsLoading(false);
        setImage(null);
        URL.revokeObjectURL(objectURL); // Clean up on error too
      };

      img.src = objectURL;

    } catch (error) {
      console.error("Error fetching image via presigned URL:", error);
      let errorMessage = "Failed to load image.";
      if (error.response) {
        errorMessage += ` Status: ${error.response.status}`;
      } else if (error.request) {
        errorMessage += " The request was made but no response was received.";
      } else {
        errorMessage += ` ${error.message}`;
      }
      setLoadError(errorMessage);
      setIsLoading(false);
      setImage(null);
    }
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setPhotoData(null);
      setImage(null);
      setAnnotations([]);
      setLoadError(null);
      setIsLoading(false);
      setApiStatus({ open: false, severity: 'info', message: '' });
    }
  }, [open]);

  // Load defect photo details when modal opens
  useEffect(() => {
    if (open && defect?.id) {
      fetchDefectPhotoDetails();
    }
  }, [open, defect]);

  // Fetch defect photo details from backend
  const fetchDefectPhotoDetails = async () => {
    console.log('[DefectViewerModal] Starting fetchDefectPhotoDetails for defect ID:', defect.id);
    setIsLoading(true);
    setLoadError(null);
    setPhotoData(null);

    try {
      console.log('[DefectViewerModal] Making API call to:', `/analytics/defect-photo-details/${defect.id}`);
      const response = await api.get(`/analytics/defect-photo-details/${defect.id}`);
      console.log('[DefectViewerModal] API response:', response.data);
      
      if (response.data.success) {
        const data = response.data.data;
        console.log('[DefectViewerModal] Setting photoData:', data);
        setPhotoData(data);
      } else {
        throw new Error(response.data.error || 'Failed to fetch defect details');
      }
    } catch (err) {
      console.error('[DefectViewerModal] Error in fetchDefectPhotoDetails:', err);
      const errorMessage = err.response?.data?.details || err.message || 'Failed to load defect photo details';
      setLoadError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
      setIsLoading(false);
    }
  };

  // Load existing annotations using the same method as Enhanced_ImageViewerModal
  const loadExistingAnnotations = async (photoId, loadedImageElement) => {
    console.log('[DefectViewerModal] Attempting to load existing annotations for photo ID:', photoId, 'With image element:', loadedImageElement);
    if (!photoId) {
      console.error('[DefectViewerModal] Cannot load annotations: photoId is undefined.');
      setApiStatus({
        open: true,
        severity: 'error',
        message: 'Error: Cannot load annotations due to missing photo ID.'
      });
      return;
    }
    
    try {
      setApiStatus({
        open: true,
        severity: 'info',
        message: 'Loading annotations...'
      });
      
      // Use boundingBoxes from photoData instead of API call
      const existingBoxes = photoData?.boundingBoxes || [];
      // Filter to show only the bounding box related to the selected defect
      const filteredBoxes = existingBoxes.filter(box => {
        // Prefer explicit linkage via defect_id
        if (box.defect_id) {
          return box.defect_id === defect.id;
        }
        // Fallback: is_primary flag from backend
        return box.is_primary === true;
      });
      console.log('[DefectViewerModal] Using filteredBoxes:', filteredBoxes);
      
      if (filteredBoxes && filteredBoxes.length > 0) {
        // Use the passed loadedImageElement for calculations
        if (!loadedImageElement) {
          console.warn('[DefectViewerModal] Image element not provided to loadExistingAnnotations.');
          setApiStatus({
            open: true,
            severity: 'warning',
            message: 'Image data missing, annotations might not display correctly. Try reopening.'
          });
          return; 
        }
        
        // Get the image display dimensions and original dimensions using loadedImageElement
        const displayWidth = Math.min(loadedImageElement.width || 0, stageSize.width || 0);
        const displayHeight = Math.min(loadedImageElement.height || 0, stageSize.height || 0);
        const originalWidth = loadedImageElement.naturalWidth || displayWidth;
        const originalHeight = loadedImageElement.naturalHeight || displayHeight;
        
        // Calculate the display-to-original scale factor (how much smaller the display is)
        const scaleFactorX = displayWidth / originalWidth;
        const scaleFactorY = displayHeight / originalHeight;
        
        // Get the image position offset in the stage
        const imagePositionX = (stageSize.width - displayWidth) / 2;
        const imagePositionY = (stageSize.height - displayHeight) / 2;
        
        console.log('[DefectViewerModal] Loading annotations with direct image element and scaling factors:', {
          displayWidth, displayHeight, originalWidth, originalHeight,
          scaleFactorX, scaleFactorY, imagePositionX, imagePositionY,
          loadedImageElementWidth: loadedImageElement.width,
          loadedImageElementHeight: loadedImageElement.height
        });
        
        // Transform bounding boxes from database format to annotation format
        const loadedAnnotations = filteredBoxes.map(box => {
          const originalBoxWidth = box.x2 - box.x1;
          const originalBoxHeight = box.y2 - box.y1;
          
          const displayX = (box.x1 * scaleFactorX) + imagePositionX;
          const displayY = (box.y1 * scaleFactorY) + imagePositionY;
          const displayAnnotationWidth = originalBoxWidth * scaleFactorX;
          const displayAnnotationHeight = originalBoxHeight * scaleFactorY;

          // Process segmentation polygon
          let scaledPolygonPoints = null;
          if (box.segmentation_polygon && Array.isArray(box.segmentation_polygon) && box.segmentation_polygon.length > 0) {
            scaledPolygonPoints = box.segmentation_polygon.map(point => ({
              x: (point[0] * scaleFactorX) + imagePositionX,
              y: (point[1] * scaleFactorY) + imagePositionY,
            }));
          }
          
          return {
            id: box.id.toString(),
            x: displayX,
            y: displayY,
            width: displayAnnotationWidth,
            height: displayAnnotationHeight,
            segmentationPolygon: scaledPolygonPoints,
            defectTypeId: box.defect_type_id,
            defectType: box.defect_type,
            status: box.status,
            severityLevelId: box.severity_level_id,
            severityLevel: box.severity_level_name,
            isAiGenerated: box.is_ai_generated,
            confidenceScore: box.confidence_score,
            notes: box.notes,
            originalX1: box.x1,
            originalY1: box.y1,
            originalX2: box.x2,
            originalY2: box.y2,
            originalDefectTypeId: box.defect_type_id,
            originalSeverityLevelId: box.severity_level_id,
            defect_id: box.defect_id,
            has_defect: box.has_defect,
            updated_by: box.updated_by,
            isPrimary: box.is_primary || false
          };
        });
        
        setAnnotations(loadedAnnotations);
        setApiStatus({
          open: true,
          severity: 'success',
          message: `Loaded ${loadedAnnotations.length} existing annotation(s)`
        });
      } else {
        setApiStatus({
          open: false
        });
      }
    } catch (error) {
      console.error('Error loading existing annotations:', error);
      setApiStatus({
        open: true,
        severity: 'error',
        message: `Error loading annotations: ${error.message || 'Unknown error'}`
      });
    }
  };

  // Load image when photoData changes
  useEffect(() => {
    console.log('[DefectViewerModal] useEffect for image loading - open:', open, 'photoData:', photoData);
    if (!open || !photoData) {
      console.log('[DefectViewerModal] Skipping image load - modal closed or no photoData');
      return;
    }
    console.log('[DefectViewerModal] Calling loadImage()');
    loadImage();
  }, [photoData, open]);

  // Load annotations when image loads AND stage is sized
  useEffect(() => {
    if (!image || !photoData?.photo?.id) return;
    if (stageSize.width === 0 || stageSize.height === 0) {
      console.log('[DefectViewerModal] Waiting for stage to be sized. Current stageSize:', stageSize);
      return;
    }
    console.log('[DefectViewerModal] Loading annotations with proper stage size:', stageSize);
    loadExistingAnnotations(photoData.photo.id, image);
  }, [image, photoData, stageSize]);

  // Adjust stage size based on container size
  useEffect(() => {
    if (!containerRef.current) {
      console.log('[DefectViewerModal] containerRef not available yet');
      return;
    }
    
    const updateSize = () => {
      const containerRect = containerRef.current.getBoundingClientRect();
      const newSize = {
        width: containerRect.width,
        height: containerRect.height
      };
      console.log('[DefectViewerModal] Updating stage size:', newSize);
      setStageSize(newSize);
    };

    // Add a small delay to ensure container is rendered
    const timeoutId = setTimeout(updateSize, 100);
    
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(containerRef.current);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [open]);

  const handleRetryLoad = () => {
    fetchDefectPhotoDetails();
  };

  const resetZoom = () => {
    setZoomLevel(1);
    setStagePosition({ x: 0, y: 0 });
    setIsZoomed(false);
  };

  const handleSafeClose = () => {
    onClose();
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setApiStatus({ ...apiStatus, open: false });
  };

  if (!defect) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleSafeClose}
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        bgcolor: 'background.paper'
      }}>
        <Box>
          <Typography variant="h6" component="div">
            Defect Visual Inspection
          </Typography>
          {defect && (
            <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {defect.defect_type_name}
              {photoData?.photo && ` • ${photoData.photo.filename || 'Image'}`}
              {image && ` • ${image.width} × ${image.height} px`}
              {defect.detection_time && ` • ${new Date(defect.detection_time).toLocaleDateString()}`}
            </Typography>
          )}
        </Box>
        <IconButton edge="end" color="inherit" onClick={handleSafeClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {isZoomed && (
            <Tooltip title="Reset Zoom">
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<ZoomOutIcon />}
                onClick={resetZoom}
              >
                Reset View
              </Button>
            </Tooltip>
          )}
        </Box>
        
        <Box 
          ref={containerRef}
          sx={{
            overflow: 'hidden',
            maxHeight: '50vh',
            height: '50vh',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 0,
            position: 'relative',
          }}
        >
          {photoData ? (
            isLoading ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography>Loading image...</Typography>
              </Box>
            ) : loadError ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}>
                <Typography color="error" sx={{ mb: 2 }}>{loadError}</Typography>
                <Button variant="contained" onClick={handleRetryLoad}>
                  Retry Loading
                </Button>
              </Box>
            ) : image ? (
              <Stage
                ref={stageRef}
                width={stageSize.width}
                height={stageSize.height}
                scaleX={zoomLevel}
                scaleY={zoomLevel}
                x={stagePosition.x}
                y={stagePosition.y}
              >
                <Layer>
                  <KonvaImage
                    ref={imageRef}
                    image={image}
                    width={Math.min(image.width, stageSize.width)}
                    height={Math.min(image.height, stageSize.height)}
                    x={(stageSize.width - Math.min(image.width, stageSize.width)) / 2}
                    y={(stageSize.height - Math.min(image.height, stageSize.height)) / 2}
                    listening={false}
                  />
                  
                  {/* Render existing annotations */}
                  {annotations.map((annotation) => {
                    const isSelected = selectedAnnotation && selectedAnnotation.id === annotation.id;
                    const boxStyle = getBoxStyle(isSelected, annotation.status, annotation.isPrimary);
                    
                    return (
                      <Group key={annotation.id}>
                        {/* Render segmentation polygon if it exists */}
                        {annotation.segmentationPolygon && (
                          <Path
                            data={pointsToPath(annotation.segmentationPolygon)}
                            fill={boxStyle.stroke}
                            opacity={0.3}
                            closed={true}
                            listening={false}
                          />
                        )}
                        {/* Primary bounding box */}
                        <Rect
                          x={annotation.x}
                          y={annotation.y}
                          width={annotation.width}
                          height={annotation.height}
                          fill="transparent"
                          stroke={boxStyle.stroke}
                          strokeWidth={boxStyle.strokeWidth}
                          dash={boxStyle.dash}
                          listening={false}
                        />
                        
                        {/* Display confidence score at top-left inside the box */}
                        {annotation.confidenceScore && (
                          <Text
                            x={annotation.x + 5}
                            y={annotation.y + 5}
                            text={`${Math.round(annotation.confidenceScore * 100)}%`}
                            fontSize={12}
                            fill="white"
                            fontStyle="bold"
                            shadowColor="black"
                            shadowBlur={5}
                            shadowOpacity={0.9}
                            listening={false}
                          />
                        )}
                        
                        {/* Display status label at top right corner */}
                        <Group>
                          <Rect
                            x={annotation.x + annotation.width - 115}
                            y={annotation.y - 20}
                            width={115}
                            height={18}
                            fill={STATUS_COLORS[annotation.status] || '#9E9E9E'}
                            cornerRadius={6}
                            opacity={0.9}
                            listening={false}
                            shadowColor="rgba(0,0,0,0.2)"
                            shadowBlur={3}
                            shadowOffset={{x: 1, y: 1}}
                          />
                          <Text
                            x={annotation.x + annotation.width - 105}
                            y={annotation.y - 14}
                            text={
                              // Show (AI) or (manual) based on isAiGenerated for draft status
                              annotation.status === 'draft'
                                ? (annotation.isAiGenerated ? 'draft (AI)' : 'draft (manual)')
                                : annotation.status // For other statuses like 'validated', 'rejected'
                            }
                            fontSize={11}
                            fontFamily="Arial"
                            fill="white"
                            fontStyle="bold"
                            listening={false}
                          />
                        </Group>
                        
                        {/* Combined defect type and severity label */}
                        <Group>
                          <Rect
                            x={annotation.x}
                            y={annotation.y + annotation.height + 2}
                            width={Math.max(120, ((annotation.defectType?.length || 0) + (annotation.severityLevel?.length || 0)) * 7 + 30)}
                            height={18}
                            fill='rgba(63, 81, 181, 0.8)'
                            cornerRadius={6}
                            opacity={0.9}
                            listening={false}
                            shadowColor="rgba(0,0,0,0.2)"
                            shadowBlur={3}
                            shadowOffset={{x: 1, y: 1}}
                          />
                          
                          <Text
                            x={annotation.x + 5}
                            y={annotation.y + annotation.height + 8}
                            text={`${annotation.defectType || 'Unknown'}${annotation.severityLevel ? ` • ${annotation.severityLevel}` : ''}`}
                            fontSize={11}
                            fontFamily="Arial"
                            fill="white"
                            fontStyle="bold"
                            width={Math.max(100, ((annotation.defectType?.length || 0) + (annotation.severityLevel?.length || 0)) * 7 + 15)}
                            ellipsis={true}
                            listening={false}
                          />
                        </Group>
                      </Group>
                    );
                  })}
                </Layer>
              </Stage>
            ) : (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}>
                <Typography>No image available</Typography>
              </Box>
            )
          ) : (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}>
              <Typography>Loading defect data...</Typography>
            </Box>
          )}
        </Box>
        
        {/* Display defect details */}
        {photoData && (
          <Paper 
            elevation={2} 
            sx={{ 
              mt: 1.5, 
              p: 1.5, 
              borderRadius: 2,
              bgcolor: 'background.paper'
            }}
          >
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                fontWeight: 600, 
                color: 'primary.main',
                borderBottom: '2px solid',
                borderColor: 'primary.light',
                pb: 0.5,
                mb: 1
              }}
            >
              Defect Information
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(5, 1fr)', 
              gap: 0.8,
              mt: 0.5 
            }}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.1 }}>
                  Type:
                </Typography>
                <Typography variant="body2">
                  {photoData.defect.defect_type_name}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.1 }}>
                  Severity:
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: photoData.defect.color_code,
                    fontWeight: 'bold',
                    textTransform: 'capitalize'
                  }}
                >
                  {photoData.defect.severity_name}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.1 }}>
                  Status:
                </Typography>
                <Box sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  bgcolor: STATUS_COLORS[photoData.defect.status] || '#9E9E9E',
                  color: 'white',
                  px: 1,
                  py: 0.2,
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}>
                  {photoData.defect.status ? photoData.defect.status.charAt(0).toUpperCase() + photoData.defect.status.slice(1) : 'N/A'}
                </Box>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.1 }}>
                  Detected:
                </Typography>
                <Typography variant="body2">
                  {new Date(photoData.defect.detection_time).toLocaleString()}
                </Typography>
              </Box>
              {photoData.defect.inspector_name && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.1 }}>
                    Inspector:
                  </Typography>
                  <Typography variant="body2">
                    {photoData.defect.inspector_name}
                  </Typography>
                </Box>
              )}
              {photoData.defect.notes && (
                <Box sx={{ gridColumn: '1 / 6', mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.3 }}>
                    Notes:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      p: 1.5,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}
                  >
                    {photoData.defect.notes}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        )}
      </DialogContent>
      
      {/* Snackbar for API status */}
      <Snackbar
        open={apiStatus.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={apiStatus.severity} sx={{ width: '100%' }}>
          {apiStatus.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default DefectViewerModal;